import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { Bell, Search, FileText, Users, Stethoscope, Download, Sparkles, Coins, Compass, DollarSign, XCircle, BarChart3, Shield, ClipboardList, Zap, Eye, Link2, Layers, ChevronDown } from 'lucide-react';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { useAdoption, PHASES } from '../shared/AdoptionJourney';
import { AdoptionLightbox } from '../shared/AdoptionJourney';
import AppSwitcher from '../shared/AppSwitcher';
import CrossAppNotifications from '../shared/CrossAppNotifications';
import ConnectionStatusIndicator from '../shared/ConnectionStatusIndicator';
import { useAuth } from '../../contexts/AuthContext';
import { useAppMode } from '../../contexts/AppModeContext';

const PHASE_COLORS = { 1: 'border-white/20 bg-white/10 text-white', 2: 'border-white/20 bg-white/10 text-white', 3: 'border-white/20 bg-white/10 text-white', 4: 'border-white/20 bg-white/10 text-white' };
const PHASE_ICONS = { 1: Eye, 2: Zap, 3: Link2, 4: Layers };

const AdoptionPhaseBadge = ({ onOpen }) => {
  const { phase } = useAdoption();
  const currentPhase = PHASES.find(p => p.id === phase);
  const Icon = PHASE_ICONS[phase] || Eye;
  return (
    <Badge variant="outline" className={cn("text-[10px] gap-1 px-2 py-1 font-medium cursor-pointer hover:opacity-80 transition-opacity", PHASE_COLORS[phase])} data-testid="adoption-phase-badge" onClick={onOpen}>
      <Icon className="h-2.5 w-2.5" /> {currentPhase?.label || 'Assist'}
    </Badge>
  );
};

const SEARCH_SUGGESTIONS = [
  { type: 'claim', icon: FileText, label: 'CLM-2026-001 — John Smith', sub: '$1,250 | E11.9, I10 | Submitted Jan 15', href: '/revq/claims' },
  { type: 'claim', icon: FileText, label: 'CLM-2026-002 — Maria Garcia', sub: '$890 | J45.20 | Pending', href: '/revq/claims' },
  { type: 'claim', icon: FileText, label: 'CLM-2026-003 — Robert Johnson', sub: '$2,100 | M54.5 | Denied', href: '/revq/denials' },
  { type: 'patient', icon: Users, label: 'John Smith', sub: 'MRN-001 | 3 active claims | $4,200 outstanding', href: '/revq/patient-billing' },
  { type: 'patient', icon: Users, label: 'Emily Davis', sub: 'MRN-004 | 1 active claim | $650 outstanding', href: '/revq/patient-billing' },
  { type: 'denial', icon: XCircle, label: 'DEN-2026-001 — Missing modifier', sub: 'Blue Cross | CLM-2026-003 | Appeal deadline: Feb 15', href: '/revq/denials' },
  { type: 'denial', icon: XCircle, label: 'DEN-2026-002 — Auth required', sub: 'Aetna | CLM-2026-005 | Appeal deadline: Feb 20', href: '/revq/denials' },
  { type: 'payer', icon: Shield, label: 'Blue Cross Blue Shield', sub: '45 active claims | 92% acceptance rate', href: '/revq/payers-rules' },
  { type: 'payer', icon: Shield, label: 'Aetna', sub: '28 active claims | 88% acceptance rate', href: '/revq/payers-rules' },
  { type: 'code', icon: ClipboardList, label: 'E11.9 — Type 2 DM', sub: 'Used in 45 encounters | $125 avg reimbursement', href: '/revq/ai-claims' },
  { type: 'code', icon: ClipboardList, label: '99214 — Office Visit Level 4', sub: 'Most common E/M code | $185 avg reimbursement', href: '/revq/ai-claims' },
];

const TYPE_LABELS = { claim: 'Claims', patient: 'Patients', denial: 'Denials', payer: 'Payers', code: 'Codes' };

const CREDIT_PLANS = [
  { id: 1, credits: 100, price: 100, label: 'Starter' },
  { id: 2, credits: 300, price: 300, label: 'Standard', popular: true },
  { id: 3, credits: 1000, price: 1000, label: 'Professional' },
];

const CREDIT_USAGE = [
  { action: 'AI Claim Generation', credits: 3, icon: Sparkles, used: 45, color: 'text-emerald-600 bg-emerald-100' },
  { action: 'AI Denial Appeal', credits: 2, icon: XCircle, used: 18, color: 'text-red-600 bg-red-100' },
  { action: 'AI Coding Review', credits: 1, icon: ClipboardList, used: 32, color: 'text-blue-600 bg-blue-100' },
  { action: 'AI Payment Posting', credits: 1, icon: DollarSign, used: 15, color: 'text-amber-600 bg-amber-100' },
];

const RevQTopbar = ({ sidebarCollapsed, onStartTour }) => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [role, setRole] = useState('manager');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [showAdoption, setShowAdoption] = useState(false);
  const searchRef = useRef(null);

  const displayName = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Practice Manager';
  const displayEmail = user?.email || 'admin@revqclinic.com';
  const initials = user?.first_name && user?.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    : displayName.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'PM';

  const balance = 412;
  const totalUsed = CREDIT_USAGE.reduce((s, u) => s + (u.used * u.credits), 0);

  const roleLabels = {
    biller: { label: 'Medical Biller', icon: FileText },
    manager: { label: 'Practice Manager', icon: Users },
    physician: { label: 'Physician', icon: Stethoscope }
  };
  const currentRole = roleLabels[role];
  const RoleIcon = currentRole.icon;

  const query = searchQuery.toLowerCase().trim();
  const filtered = query.length > 0 ? SEARCH_SUGGESTIONS.filter(s => s.label.toLowerCase().includes(query) || s.sub.toLowerCase().includes(query)) : [];
  const grouped = filtered.reduce((acc, s) => { if (!acc[s.type]) acc[s.type] = []; acc[s.type].push(s); return acc; }, {});

  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchFocused(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-14 bg-black flex items-center justify-between px-5 z-50" data-testid="revq-topbar">
        {/* Left: App Switcher + Role Toggle + Search */}
        <div className="flex items-center gap-4 flex-1">
          <AppSwitcher currentApp="revq" collapsed={false} variant="topbar" />

          <div className="h-5 w-px bg-white/15" />
          <div className="relative w-full max-w-lg" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
            <Sparkles className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-white/30" />
            <Input placeholder="Search claims, patients, denials, payers, codes..." className="pl-9 pr-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm h-9 focus-visible:ring-white/20" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} data-testid="ai-search-input" />
            {searchFocused && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden max-h-[400px] overflow-y-auto" data-testid="ai-search-dropdown">
                {query.length === 0 ? (
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2"><Sparkles className="h-3.5 w-3.5 text-violet-500" /><span className="font-medium">AI-powered search</span></div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(isDemo ? ['John Smith', 'CLM-2026', 'Blue Cross', '99214', 'Denied claims'] : ['CLM-2026', 'Blue Cross', '99214', 'Denied claims']).map((h, i) => (
                        <button key={i} onClick={() => setSearchQuery(h)} className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors">{h}</button>
                      ))}
                    </div>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="p-4 text-center"><p className="text-sm text-slate-500">No results for "{searchQuery}"</p></div>
                ) : (
                  Object.entries(grouped).map(([type, items]) => (
                    <div key={type}>
                      <div className="px-3 py-2 bg-slate-50 border-b"><p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{TYPE_LABELS[type]}</p></div>
                      {items.slice(0, 3).map((item, i) => {
                        const Icon = item.icon;
                        return (
                          <button key={i} onClick={() => { setSearchQuery(''); setSearchFocused(false); navigate(item.href); }} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left" data-testid={`search-result-${type}-${i}`}>
                            <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-slate-600" /></div>
                            <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800 truncate">{item.label}</p><p className="text-xs text-slate-400 truncate">{item.sub}</p></div>
                          </button>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1 px-2.5 py-1 text-[10px] font-medium cursor-pointer hover:bg-white/10 border-white/20 text-white transition-colors" onClick={onStartTour} data-testid="start-tour-btn">
            <Compass className="h-2.5 w-2.5" /> Tour
          </Badge>

          <AdoptionPhaseBadge onOpen={() => setShowAdoption(true)} />

          <Badge variant="outline" className="gap-1.5 px-2.5 py-1 text-[11px] font-semibold cursor-pointer hover:bg-white/10 border-amber-500/40 text-amber-400 transition-colors" onClick={() => setShowCredits(true)} data-testid="credits-badge">
            <Coins className="h-3 w-3" /> {balance} Credits
          </Badge>

          <ConnectionStatusIndicator />

          <div className="h-4 w-px bg-white/15" />

          <CrossAppNotifications currentApp="revq" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 h-8 hover:bg-white/10" data-testid="user-profile-trigger">
                <div className="h-7 w-7 rounded-full bg-white/15 flex items-center justify-center text-[10px] font-bold text-white" data-testid="user-initials">{initials}</div>
                <div className="text-left hidden sm:block">
                  <p className="text-xs font-medium text-white" data-testid="user-display-name">{displayName}</p>
                </div>
                <ChevronDown className="h-3 w-3 text-white/50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel className="font-normal"><p className="text-sm font-medium" data-testid="dropdown-user-name">{displayName}</p><p className="text-xs text-muted-foreground" data-testid="dropdown-user-email">{displayEmail}</p></DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/revq/settings')}>Settings</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600" onClick={() => { signOut(); navigate('/'); }} data-testid="sign-out-btn">Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Credits Lightbox */}
      <Dialog open={showCredits} onOpenChange={setShowCredits}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="credits-lightbox">
          <div className="bg-black p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div><p className="text-2xl font-bold">{balance}</p><p className="text-sm text-slate-400">Credits remaining</p></div>
              <Badge className="bg-white/10 text-white text-xs">{totalUsed} used this month</Badge>
            </div>
            <DialogTitle className="sr-only">revQ Credits</DialogTitle>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <p className="text-sm font-semibold mb-3">Credit Usage by AI Action</p>
              <div className="space-y-2">
                {CREDIT_USAGE.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200" data-testid={`credit-usage-${i}`}>
                      <div className="flex items-center gap-2.5">
                        <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", item.color.split(' ')[1])}><Icon className={cn("h-4 w-4", item.color.split(' ')[0])} /></div>
                        <div><p className="text-sm font-medium">{item.action}</p><p className="text-[11px] text-muted-foreground">{item.credits} credit{item.credits > 1 ? 's' : ''} per action</p></div>
                      </div>
                      <span className="text-sm font-semibold">{item.used * item.credits} used</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold mb-3">Add Credits</p>
              <div className="grid grid-cols-3 gap-2">
                {CREDIT_PLANS.map(plan => (
                  <button key={plan.id} onClick={() => { toast.success(`${plan.credits} credits added`); setShowCredits(false); }}
                    className={cn("relative p-3 rounded-xl border-2 text-center hover:border-slate-400 transition-all", plan.popular ? "border-slate-900" : "border-slate-200")} data-testid={`credit-plan-${plan.id}`}>
                    {plan.popular && <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-black text-white text-[9px] px-2 py-0">Popular</Badge>}
                    <p className="text-lg font-bold">{plan.credits}</p>
                    <p className="text-xs text-muted-foreground">${plan.price}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AdoptionLightbox open={showAdoption} onOpenChange={setShowAdoption} />
    </>
  );
};

export default RevQTopbar;
